import { useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { Gauge, ArchiveRestore, SlidersHorizontal, LogOut, Search, Eye, Cloud, HardDrive } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCloud } from "@/context/CloudContext";

export default function Shell() {
  const { user, logout, readOnly } = useAuth();
  const { state: cloudState } = useCloud();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const initials = (user?.display_name || user?.username || "BDX")
    .split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();

  const connected = cloudState?.status === "Connected";
  const recovery = cloudState?.mode === "recovery";
  const onDashboard = location.pathname === "/";
  const onSettings = location.pathname.startsWith("/settings");
  const onArchived = location.pathname.startsWith("/archived");

  return (
    <div className="min-h-screen bg-[#f4f3f2]">
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white shadow-[0_1px_5px_rgba(16,19,21,0.035)]">
        <div className="mx-auto flex min-h-[88px] w-full max-w-[1680px] items-center gap-3 px-5 sm:px-7 lg:gap-4 lg:px-10">
          <div className="flex min-w-0 items-center gap-3.5">
            <img src="/logos/bdx.png" alt="BDX Group" className="h-11 w-auto shrink-0 object-contain" data-testid="shell-bdx-logo" />
            <div className="hidden border-l-2 border-[#79559d] pl-3 xl:block">
              <div className="whitespace-nowrap font-heading text-[15px] font-semibold leading-tight text-gray-950">Project Lifecycle Manager</div>
              <div className="mt-1 whitespace-nowrap text-[10px] font-semibold uppercase text-gray-500" style={{ letterSpacing: "0.14em" }}>BDX Operations · SSH &amp; MMC</div>
            </div>
          </div>

          <button
            data-testid="nav-dashboard"
            onClick={() => navigate("/")}
            className={`flex h-12 shrink-0 items-center gap-2 rounded-md border px-4 text-[15px] transition-[color,background-color,border-color] duration-150 sm:px-4 ${
              onDashboard
                ? "border-[#c3aed4] bg-[#f3eff7] font-semibold text-[#604184]"
                : "border-gray-300 bg-white font-medium text-gray-700 shadow-sm hover:border-[#9b7ab8] hover:bg-[#faf8fc] hover:text-[#604184]"
            }`}
            title="Return to the Project Streams dashboard"
          >
            <Gauge size={18} strokeWidth={1.8} />
            <span className="hidden sm:inline">Dashboard</span>
          </button>

          <form onSubmit={doSearch} className="relative min-w-0 max-w-md flex-1" data-testid="global-search-form">
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" strokeWidth={1.5} />
            <input
              data-testid="global-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search project number or address"
              className="h-12 w-full rounded-md border border-gray-200 bg-[#f7f6f5] pl-11 pr-4 text-[15px] text-gray-900 transition-[background-color,border-color,box-shadow] duration-150 placeholder:text-gray-400 focus:border-[#765297] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#765297]/10"
            />
          </form>

          <div className="ml-auto flex items-center gap-2.5 lg:gap-3">
            <div
              data-testid="shell-cloud-state"
              className={`hidden items-center gap-2.5 rounded-md border px-3.5 py-2.5 md:flex ${recovery ? "border-amber-300 bg-amber-50" : "border-gray-200 bg-[#f7f6f5]"}`}
              title={recovery ? "Offline Recovery Edition — local point-in-time copy, no cloud connection" : "BDX Cloud Platform"}
            >
              {recovery ? <HardDrive size={17} strokeWidth={1.8} className="text-amber-600" />
                : <Cloud size={17} strokeWidth={1.8} className={connected ? "text-emerald-600" : "text-amber-500"} />}
              <div className="hidden leading-none lg:block">
                <div className={`text-[9px] font-semibold uppercase ${recovery ? "text-amber-700" : "text-gray-500"}`} style={{ letterSpacing: "0.12em" }}>System</div>
                <div className={`mt-0.5 text-xs font-semibold ${recovery ? "text-amber-800" : "text-gray-800"}`}>{recovery ? "OFFLINE LOCAL RECOVERY" : connected ? "CLOUD CONNECTED" : "RECONNECTING"}</div>
              </div>
            </div>

            {readOnly && (
              <span data-testid="read-only-badge" className="hidden items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-[10px] font-semibold uppercase text-amber-800 sm:inline-flex" style={{ letterSpacing: "0.08em" }}>
                <Eye size={12} strokeWidth={2} /> Read Only Access
              </span>
            )}

            <div className="flex items-center gap-2.5 border-l border-gray-200 pl-2.5 sm:pl-3.5">
              <div className="flex h-11 w-11 items-center justify-center rounded-md border border-[#d9cde5] bg-[#f3eff7] font-heading text-sm font-bold text-[#604184]" data-testid="current-user-avatar">{initials}</div>
              <div className="hidden text-left lg:block">
                <div data-testid="current-user" className="whitespace-nowrap text-[15px] font-semibold leading-tight text-gray-950">{user?.display_name}</div>
                <div className="mt-0.5 text-[10px] font-medium uppercase text-gray-500" style={{ letterSpacing: "0.08em" }}>{user?.role === "admin" ? "Administrator" : "Viewer"}</div>
              </div>
            </div>

            <div className="flex items-center gap-1 rounded-md border border-gray-200 bg-white p-1 shadow-sm">
              <button
                data-testid="nav-archived-projects"
                onClick={() => navigate("/archived")}
                className={`flex h-10 w-10 items-center justify-center rounded transition-[color,background-color] duration-150 ${onArchived ? "bg-[#f3eff7] text-[#604184]" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"}`}
                title="Archived Projects" aria-label="Archived Projects"
              >
                <ArchiveRestore size={17} strokeWidth={1.6} />
              </button>
              <button
                data-testid="nav-settings"
                onClick={() => navigate("/settings")}
                className={`flex h-10 w-10 items-center justify-center rounded transition-[color,background-color] duration-150 ${onSettings ? "bg-[#f3eff7] text-[#604184]" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"}`}
                title="Settings" aria-label="Settings"
              >
                <SlidersHorizontal size={17} strokeWidth={1.6} />
              </button>
            </div>

            <button data-testid="logout-btn" onClick={logout} className="flex h-12 w-12 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 shadow-sm transition-[color,background-color,border-color,transform] duration-150 hover:border-[#9b7ab8] hover:bg-[#faf8fc] hover:text-[#604184] active:translate-y-px" title="Sign out" aria-label="Sign out">
              <LogOut size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1680px] px-5 py-7 sm:px-7 lg:px-10 lg:py-9">
        <Outlet />
      </main>
    </div>
  );
}
